import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, Timestamp } from "firebase/firestore";
import { db } from "@/services/firebase";
import { tcol } from "@/domain/tenantPath";

/**
 * 최신 RQC 드리프트 검사 결과(rqc_drift_log, checked_at desc 1건) 구독.
 *
 * rqc_drift_log: cron rqcMonthlyDriftCheck(평일 11/19시)가 1 doc/회 append.
 *   - drift_count : 불일치 감지된 개월 수
 *   - drift_months: ["yyyy-MM", …]
 *   - backfills[] : 월별 자동 보정 결과 (succeeded 합산 → 보정 건수)
 * 읽기 권한: shipment.view || dispatch.view (firestore.rules).
 *
 * enabled=false면 구독하지 않음(권한/설정 게이팅용).
 */
export interface RqcDriftStatus {
  driftCount: number;
  driftMonths: string[];
  backfillSucceeded: number;
  checkedAt: Date | null;
}

export function useRqcDriftStatus(enabled = true) {
  const [status, setStatus] = useState<RqcDriftStatus | null>(null);

  useEffect(() => {
    if (!enabled) { setStatus(null); return; }
    const q = query(collection(db, tcol("rqc_drift_log")), orderBy("checked_at", "desc"), limit(1));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const d = snap.docs[0]?.data();
        if (!d) { setStatus(null); return; }
        const backfills = Array.isArray(d.backfills) ? d.backfills : [];
        setStatus({
          driftCount: Number(d.drift_count) || 0,
          driftMonths: Array.isArray(d.drift_months) ? d.drift_months : [],
          backfillSucceeded: backfills.reduce(
            (sum: number, b) => sum + (Number(b?.succeeded) || 0),
            0,
          ),
          checkedAt: d.checked_at instanceof Timestamp ? d.checked_at.toDate() : null,
        });
      },
      () => setStatus(null),
    );
    return unsub;
  }, [enabled]);

  return status;
}
